import { data, Form, Link } from 'react-router'
import { AuthorityGauge } from '#app/components/brand/authority-gauge.tsx'
import { FaqAccordion } from '#app/components/brand/faq.tsx'
import { NetworkGraph } from '#app/components/brand/network-graph.tsx'
import {
	Eyebrow,
	Heading,
	SectionKicker,
} from '#app/components/brand/section.tsx'
import { TurnstileWidget } from '#app/components/turnstile.tsx'
import { Button } from '#app/components/ui/button.js'
import { Icon, type IconName } from '#app/components/ui/icon.js'
import { buildMeta, faqPageJsonLd, siteJsonLd } from '#app/utils/seo.ts'
import { type Route } from './+types/index.ts'
import { Footer } from './components/footer'
import Header from './components/header'

export const meta: Route.MetaFunction = () => {
	return [
		...buildMeta({
			title: 'Competitor Backlinks: Link intelligence, demystified',
			description:
				"See who's linking to your competitors and why it matters. No $129-a-month cockpit required. Enter a domain and get a clear map of their backlink network in seconds.",
			path: '/',
			image: '/img/og/home.png',
		}),
		{ 'script:ld+json': siteJsonLd() },
		{ 'script:ld+json': faqPageJsonLd(HOME_FAQS) },
	]
}

// Homepage-level FAQs — product/brand framing, deliberately distinct from the
// checker's tool-level questions so the two FAQPage blocks don't duplicate. One
// array feeds both the visible accordion and the JSON-LD.
const HOME_FAQS: ReadonlyArray<{ q: string; a: string }> = [
	{
		q: 'What is Competitor Backlinks?',
		a: 'A focused tool for reading a competitor’s backlink profile and finding your gap: the sites linking to them but not to you. It does that one job clearly, without the price or complexity of a full enterprise SEO suite.',
	},
	{
		q: 'Do I need an account to try it?',
		a: 'No. The free backlink checker runs with no account and no credit card. Enter a competitor domain and you get their referring-domain map and top linking sites immediately.',
	},
	{
		q: 'What is free and what costs money?',
		a: 'Reading a competitor’s backlink map and seeing your gap is free, with no account. You pay when you want us to close that gap for you, which is $299 a month and cancellable any time, or when you want to do it yourself with the scored report and CSV, which is a one-time payment from $9.',
	},
	{
		q: 'How current is the backlink data?',
		a: 'It comes from a live, continuously crawled backlink index, so you see referring domains as they exist now rather than a stale monthly snapshot.',
	},
	{
		q: 'How is this different from Ahrefs or Semrush?',
		a: 'Those suites are powerful and priced for full-time SEOs. This tool does one thing well, reading competitor backlinks and finding your gap, and explains it in plain language for a fraction of the cost.',
	},
]

export async function loader({}: Route.LoaderArgs) {
	// No plan catalog here any more: the homepage sells the service and links to
	// /pricing for the one-time options, so it no longer needs a purchasable-plan
	// query on every render.
	return data({ siteKey: process.env.TURNSTILE_SITE_KEY ?? null })
}

const stakes = [
	{
		stat: 'Most',
		text: "of a competitor's links are reproducible tactics you could earn too.",
	},
	{
		stat: '$0',
		text: 'is what it should cost to simply see who links to a rival. So that’s our price.',
	},
	{
		stat: '60 sec',
		text: 'from pasting a domain to reading a briefing you actually understand.',
	},
]

const steps: { n: string; icon: IconName; title: string; text: string }[] = [
	{
		n: '01',
		icon: 'magnifying-glass',
		title: 'Paste a competitor’s domain',
		text: 'No signup, no setup. Just the URL of any competitor you want to understand.',
	},
	{
		n: '02',
		icon: 'share-2',
		title: 'We map the inbound network',
		text: 'Every referring domain converges on the target, sized by authority and coloured by what’s new or lost.',
	},
	{
		n: '03',
		icon: 'clipboard-list',
		title: 'Read the briefing',
		text: 'A short summary tells you what’s driving their links, and which ones you can go get.',
	},
]

const features: {
	icon: IconName
	title: string
	text: string
	className: string
}[] = [
	{
		icon: 'share-2',
		title: 'Inbound network graph',
		text: 'Watch every referring domain flow in toward your competitor at a glance.',
		className: 'bg-brand/[0.12] text-brand',
	},
	{
		icon: 'chart-no-axes-column',
		title: 'New vs lost timeline',
		text: 'Spot links they just gained, and the ones quietly slipping away.',
		className: 'bg-gained/[0.13] text-gained-ink',
	},
	{
		icon: 'logs',
		title: 'Anchor-text distribution',
		text: 'See what text people link with, and why the mix matters.',
		className: 'bg-brand-light/[0.13] text-brand-light',
	},
	{
		icon: 'shield-check',
		title: 'Authority gauge',
		text: 'One honest score for how strong a link profile really is.',
		className: 'bg-lost/[0.11] text-lost',
	},
]

const diffs = [
	{ them: 'Dense cockpit dashboards', us: 'One clear thing at a time' },
	{ them: 'Blue-and-orange data grids', us: 'Calm, editorial and human' },
	{ them: 'Jargon with no explanation', us: 'Every term defined' },
	{
		them: '$129+/mo just to start',
		us: 'Free to start, honest about upgrades',
	},
	{ them: '“More data than anyone”', us: 'More clarity than anyone' },
]

// Our own product claims, written in our voice. Deliberately NOT testimonials:
// no attribution, no star ratings, no quotation marks. Swap this section for
// real customer quotes only once we actually have them.
const productClaims = [
	{
		title: 'No spreadsheet hangover',
		text: 'You get a written briefing that says what a competitor is doing and what to do about it, not a 12-tab export to decode on your own.',
	},
	{
		title: 'Plain English, not jargon',
		text: 'Every metric on the page defines itself. If a number does not change what you would do next, we leave it out.',
	},
	{
		title: 'Useful before you pay',
		text: 'The free checker runs a real backlink profile with no account. We say so when the free tool is all you need.',
	},
]

const content = [
	{
		tag: 'Guide',
		title: 'How to find your competitors’ backlinks, without the jargon',
		href: '/guides/find-competitor-backlinks',
		image: '/img/og/find-competitor-backlinks.png',
	},
	{
		tag: 'Comparison',
		title: 'Semrush vs Ahrefs for competitor backlinks',
		href: '/best-tools/semrush-vs-ahrefs',
		image: '/img/og/semrush-vs-ahrefs.png',
	},
	{
		tag: 'Course',
		title: 'Run backlink research yourself with AI: the workshop',
		href: '/course',
		image: '/img/og/course.png',
	},
]

const promises = [
	'We give real value before ever asking for your email',
	'We recommend tools we’d actually use, and say when free is enough',
	'No fake countdowns, no “27 people viewing”, no exit traps',
	'Every affiliate link is clearly, calmly disclosed',
]

function TeaserGraphic() {
	return (
		<div className="border-hairline relative h-[130px] overflow-hidden border-b bg-gradient-to-br from-[#F3F0FF] to-[#FBF9F5]">
			<svg
				width="100%"
				height="100%"
				viewBox="0 0 300 130"
				preserveAspectRatio="xMidYMid slice"
				className="absolute inset-0 opacity-85"
			>
				<g stroke="#5B4BFF" strokeWidth="1" opacity="0.3">
					<line x1="150" y1="65" x2="40" y2="20" />
					<line x1="150" y1="65" x2="260" y2="30" />
					<line x1="150" y1="65" x2="70" y2="110" />
					<line x1="150" y1="65" x2="250" y2="105" />
				</g>
				<g fill="#7A5CFF">
					<circle cx="40" cy="20" r="4" />
					<circle cx="260" cy="30" r="5" />
					<circle cx="70" cy="110" r="4" />
					<circle cx="250" cy="105" r="4" />
				</g>
				<circle cx="150" cy="65" r="8" fill="#5B4BFF" />
			</svg>
		</div>
	)
}

export default function Index({ loaderData }: Route.ComponentProps) {
	return (
		<div className="bg-canvas text-ink min-h-screen font-sans text-base leading-normal">
			<Header />

			{/* HERO */}
			<section className="mx-auto grid max-w-[1200px] items-center gap-14 px-8 pt-11 pb-16 lg:grid-cols-[1.05fr_0.95fr]">
				<div>
					<div className="border-hairline mb-6 inline-flex items-center gap-2 rounded-full border bg-white px-3 py-1.5 text-[13px] font-medium text-[#6B6B72]">
						<span className="bg-gained size-[7px] rounded-full" />
						Free tool · No account · No jargon
					</div>
					<Heading
						as="h1"
						className="text-[clamp(40px,5.5vw,64px)] leading-[1.02] tracking-[-0.03em]"
					>
						Link intelligence,
						<br />
						<span className="text-brand">demystified.</span>
					</Heading>
					<p className="mt-5 mb-7 max-w-[500px] text-xl leading-relaxed text-pretty text-[#57565E]">
						See who&rsquo;s linking to your competitors and why it matters. No
						$129-a-month cockpit required. Enter a domain and get a clear map of
						their backlink network in seconds.
					</p>

					<Form
						method="post"
						action="/free-backlink-checker"
						className="border-hairline flex max-w-[500px] flex-col gap-2.5 rounded-2xl border bg-white p-2 shadow-[0_1px_2px_rgba(21,21,25,.04),0_14px_34px_-20px_rgba(21,21,25,.2)]"
					>
						<div className="flex gap-2.5">
							<div className="flex flex-1 items-center gap-2.5 pl-3.5">
								<Icon
									name="magnifying-glass"
									className="size-[18px] text-[#B5B1A8]"
								/>
								<label htmlFor="hero-domain" className="sr-only">
									Competitor domain to analyze
								</label>
								<input
									id="hero-domain"
									name="domain"
									placeholder="competitor-domain.com"
									spellCheck={false}
									autoComplete="off"
									className="text-ink w-full flex-1 border-none bg-transparent py-3 font-mono text-base outline-none"
								/>
							</div>
							<Button
								type="submit"
								size="lg"
								className="h-auto rounded-[11px] px-6 py-3 text-[15.5px] whitespace-nowrap"
							>
								Map the links
							</Button>
						</div>
						{loaderData.siteKey ? (
							<TurnstileWidget siteKey={loaderData.siteKey} />
						) : null}
					</Form>
					{/* Not everyone arrives thinking about a competitor. Plenty land
					    here wanting their own inbound links first, and bounce off a form
					    asking for a domain they have not chosen yet. */}
					<p className="mt-4 text-[14px] text-[#8A867D]">
						Rather start with your own site?{' '}
						<Link
							to="/who-links-to-my-website"
							className="text-brand font-medium underline underline-offset-2"
						>
							See who links to your website
						</Link>
						.
					</p>
					<div className="mt-5 flex flex-wrap items-center gap-5 text-[13.5px] text-[#8A867D]">
						<span className="flex items-center gap-1.5">
							<Icon name="check" className="text-gained-ink size-3.5" />
							No credit card
						</span>
						<span className="flex items-center gap-1.5">
							<Icon name="check" className="text-gained-ink size-3.5" />
							No account required
						</span>
					</div>
				</div>

				{/* HERO VISUAL */}
				<div className="relative">
					<div className="overflow-hidden rounded-[22px] border border-[#E9E4F5] bg-gradient-to-b from-[#FFFEFC] to-[#F6F2FF] shadow-[0_30px_60px_-32px_rgba(91,75,255,.35)]">
						<div className="flex items-center gap-2 border-b border-[#EFEAF7] px-4 py-3">
							<span className="size-2.5 rounded-full bg-[#E4DFF6]" />
							<span className="size-2.5 rounded-full bg-[#E4DFF6]" />
							<span className="size-2.5 rounded-full bg-[#E4DFF6]" />
							<span className="ml-2 font-mono text-xs text-[#9A87D8]">
								backlink network · live
							</span>
						</div>
						<div className="h-[340px] w-full">
							<NetworkGraph />
						</div>
					</div>
					<div className="animate-float border-hairline absolute -bottom-[18px] -left-[18px] rounded-2xl border bg-white px-[18px] py-3.5 shadow-[0_12px_30px_-16px_rgba(21,21,25,.3)]">
						<div className="text-[11.5px] font-medium text-[#8A867D]">
							New links · 30 days
						</div>
						<div className="text-gained-ink font-mono text-[22px] font-semibold">
							+214
						</div>
					</div>
				</div>
			</section>

			{/* STAKES */}
			<section className="bg-ink text-[#F3F1EC]">
				<div className="mx-auto max-w-[960px] px-8 py-[76px] text-center">
					<Eyebrow tone="brand-light" className="mb-5 tracking-[0.1em]">
						The thing nobody tells you
					</Eyebrow>
					<Heading className="mx-auto text-[clamp(28px,4vw,42px)] leading-[1.12]">
						Your competitors&rsquo; best links are a map. Most people never
						learn to read it.
					</Heading>
					<p className="mx-auto mt-5 mb-11 max-w-[660px] text-[19px] leading-relaxed text-pretty text-[#B9B6AE]">
						Every link pointing at a competitor is a tested, repeatable tactic.
						It is a page that will probably link to you too. The enterprise
						tools have this data, but they bury it under dense dashboards,
						jargon, and a price tag built for agencies. We think that&rsquo;s
						backwards.
					</p>
					<div className="mx-auto grid max-w-[760px] gap-6 text-left sm:grid-cols-3">
						{stakes.map((s) => (
							<div key={s.stat}>
								<div className="text-brand-light mb-2 font-mono text-[26px] font-semibold">
									{s.stat}
								</div>
								<div className="text-[15px] leading-relaxed text-[#C9C6BE]">
									{s.text}
								</div>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* HOW IT WORKS */}
			<section
				id="how-it-works"
				className="mx-auto max-w-[1080px] px-8 pt-[84px]"
			>
				<div className="mb-12 text-center">
					<Eyebrow className="mb-3.5">How it works</Eyebrow>
					<Heading className="text-[clamp(28px,4vw,40px)]">
						From a domain to a briefing in three steps
					</Heading>
				</div>
				<div className="grid gap-6 md:grid-cols-3">
					{steps.map((st) => (
						<div
							key={st.n}
							className="border-hairline rounded-2xl border bg-white p-7"
						>
							<div className="mb-4 flex items-center justify-between">
								<div className="bg-brand-wash text-brand flex size-11 items-center justify-center rounded-xl">
									<Icon name={st.icon} className="size-5" />
								</div>
								<span className="font-mono text-[26px] font-semibold text-[#E0DBD1]">
									{st.n}
								</span>
							</div>
							<div className="font-display mb-2 text-[19px] font-semibold">
								{st.title}
							</div>
							<p className="text-[15px] leading-relaxed text-[#57565E]">
								{st.text}
							</p>
						</div>
					))}
				</div>
			</section>

			{/* CHARTS THAT EXPLAIN THEMSELVES */}
			<section className="mx-auto max-w-[1080px] px-8 pt-[84px]">
				<div className="mb-12 text-center">
					<Eyebrow className="mb-3.5">What you&rsquo;ll understand</Eyebrow>
					<Heading className="mb-3 text-[clamp(28px,4vw,40px)]">
						Charts that explain themselves
					</Heading>
					<p className="mx-auto max-w-[560px] text-lg text-pretty text-[#57565E]">
						Every visual is labelled in clear terms and annotated with what it
						means. You never get a raw grid dumped on you.
					</p>
				</div>
				<div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
					{features.map((f) => (
						<div
							key={f.title}
							className="border-hairline rounded-2xl border bg-white p-6"
						>
							<div
								className={`mb-4 flex size-10 items-center justify-center rounded-xl ${f.className}`}
							>
								<Icon name={f.icon} className="size-5" />
							</div>
							<div className="font-display mb-1.5 text-[17px] font-semibold">
								{f.title}
							</div>
							<p className="text-[14.5px] leading-relaxed text-[#57565E]">
								{f.text}
							</p>
						</div>
					))}
				</div>
				{/* live authority gauge sample */}
				<div className="border-hairline mx-auto mt-5 max-w-[360px] rounded-2xl border bg-white p-6">
					<AuthorityGauge value={84} />
				</div>
			</section>

			{/* DIFFERENTIATOR */}
			<section className="mx-auto max-w-[1000px] px-8 pt-[84px]">
				<div className="mb-11 text-center">
					<Eyebrow className="mb-3.5">Why we&rsquo;re different</Eyebrow>
					<Heading className="text-[clamp(28px,4vw,40px)]">
						Not another cockpit dashboard
					</Heading>
				</div>
				<div className="border-hairline overflow-hidden rounded-[18px] border bg-white">
					<div className="border-hairline grid grid-cols-2 border-b bg-[#F7F4EE]">
						<div className="px-6 py-[18px] text-[13px] font-semibold tracking-[0.04em] text-[#8A867D] uppercase">
							The enterprise tools
						</div>
						<div className="bg-brand-wash text-brand font-display px-6 py-[18px] text-base font-bold">
							competitorbacklinks.com
						</div>
					</div>
					{diffs.map((d) => (
						<div
							key={d.them}
							className="grid grid-cols-2 border-b border-[#F1EDE4] last:border-b-0"
						>
							<div className="flex items-center gap-2.5 px-6 py-4 text-[15px] text-[#8A867D]">
								<Icon name="cross-1" className="size-4 text-[#C4BFB4]" />
								{d.them}
							</div>
							<div className="bg-brand-wash flex items-center gap-2.5 px-6 py-4 text-[15px] font-medium text-[#2B2A31]">
								<Icon name="check" className="text-gained-ink size-4" />
								{d.us}
							</div>
						</div>
					))}
				</div>
			</section>

			{/* WHAT WE PROMISE */}
			<section className="mx-auto max-w-[1080px] px-8 pt-[84px]">
				<div className="mb-6">
					<Eyebrow tone="muted">What we promise</Eyebrow>
				</div>
				<div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
					{productClaims.map((p) => (
						<div
							key={p.title}
							className="border-hairline rounded-2xl border bg-white p-7"
						>
							<div className="bg-brand-wash text-brand mb-4 flex size-9 items-center justify-center rounded-full">
								<Icon name="check" className="size-4" />
							</div>
							<div className="font-display text-ink mb-2 text-[17px] font-bold">
								{p.title}
							</div>
							<p className="text-[15.5px] leading-relaxed text-pretty text-[#57565E]">
								{p.text}
							</p>
						</div>
					))}
				</div>
			</section>

			{/* PRICING — the offer, not a menu.
			    Until Aug 2026 this rendered the three report tiers under "Pay once,
			    get the insights you need. One-time reports, no subscription." That
			    is the offer roadmap v3.0 retired, and it contradicted /pricing the
			    moment that page changed. The homepage is the highest-traffic surface
			    we have, so it states the service and sends people to one place.
			    Deliberately NOT touching the hero or the checker above: those are
			    what rank, and this section sits well below them. */}
			<section id="pricing" className="mx-auto max-w-[1080px] px-8 pt-[84px]">
				<div className="border-hairline rounded-[28px] border bg-white p-8 text-center sm:p-12">
					<Eyebrow className="mb-3.5">Done-for-you link building</Eyebrow>
					<Heading className="mb-3 text-[clamp(28px,4vw,40px)]">
						Or we go and get the links for you
					</Heading>
					<p className="mx-auto max-w-[560px] text-lg text-pretty text-[#57565E]">
						The checker shows you the gap. If you would rather not spend the
						next six months closing it yourself, we do the work: a steady drip
						of placements from the sites already linking to your competitors,
						starting from wherever your profile actually is today.
					</p>
					<div className="mt-6 flex flex-wrap items-baseline justify-center gap-x-3 gap-y-1">
						<span className="font-display text-[40px] leading-none font-semibold">
							$299
						</span>
						<span className="text-[15px] text-[#57565E]">
							per month, cancel any time
						</span>
					</div>
					<div className="mt-7 flex flex-wrap items-center justify-center gap-3">
						<Button asChild size="lg">
							<Link to="/get-started">
								Get your gap fixed
								<Icon name="arrow-right" className="size-4" />
							</Link>
						</Button>
						<Button asChild size="lg" variant="outline">
							<Link to="/pricing">See what is included</Link>
						</Button>
					</div>
					<p className="mt-5 text-[14px] text-[#8A867D]">
						Prefer to buy once? Gap reports from $9 and one-off foundation packs
						are on the{' '}
						<Link to="/pricing" className="text-brand font-medium">
							pricing page
						</Link>
						.
					</p>
				</div>
			</section>

			{/* CONTENT TEASERS */}
			<section className="mx-auto max-w-[1080px] px-8 pt-[84px]">
				<div className="mb-8 flex flex-wrap items-end justify-between gap-4">
					<div>
						<Eyebrow className="mb-3">Learn as you go</Eyebrow>
						<Heading className="text-[clamp(26px,3.5vw,36px)]">
							Guides, comparisons &amp; a course
						</Heading>
					</div>
					<Link to="/guides" className="text-brand text-[15px] font-semibold">
						Browse all guides →
					</Link>
				</div>
				<div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
					{content.map((c) => (
						<Link
							key={c.title}
							to={c.href}
							className="border-hairline hover:border-brand text-ink block overflow-hidden rounded-2xl border bg-[#FDFCFA] transition-colors"
						>
							{c.image ? (
								<img
									src={c.image}
									alt={c.title}
									width={1200}
									height={630}
									loading="lazy"
									className="border-hairline aspect-[1200/630] w-full border-b object-cover"
								/>
							) : (
								<TeaserGraphic />
							)}
							<div className="p-5">
								<div className="text-brand mb-2.5 font-mono text-[11px] font-semibold tracking-[0.05em] uppercase">
									{c.tag}
								</div>
								<div className="font-display text-[17px] leading-tight font-semibold text-pretty">
									{c.title}
								</div>
							</div>
						</Link>
					))}
				</div>
			</section>

			{/* TRUST PROMISE */}
			<section className="mx-auto max-w-[900px] px-8 pt-[84px]">
				<div className="rounded-[18px] border border-[#DCEFE6] bg-[#F0FAF5] px-9 py-10">
					<Eyebrow tone="muted" className="text-gained-ink mb-3.5">
						Our promise
					</Eyebrow>
					<Heading className="mb-5 text-[clamp(24px,3.5vw,32px)] text-[#1B3A2C]">
						No dark patterns. Ever.
					</Heading>
					<div className="grid gap-3 sm:grid-cols-2">
						{promises.map((p) => (
							<div
								key={p}
								className="flex h-full items-center gap-3 rounded-xl border border-[#CFE9DC] bg-white/60 px-4 py-3.5 text-[15px] leading-relaxed text-[#2B4A3B]"
							>
								<Icon
									name="check"
									className="text-gained-ink size-4 shrink-0"
								/>
								<span className="text-pretty">{p}</span>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* FAQ */}
			<section
				className="mx-auto grid max-w-[1080px] gap-10 px-8 pt-[84px] lg:grid-cols-[0.62fr_1.38fr] lg:gap-14"
				aria-label="Frequently asked questions"
			>
				<div>
					<SectionKicker>Questions</SectionKicker>
					<Heading className="mt-4 text-[clamp(24px,3.4vw,34px)]">
						The short answers
					</Heading>
					<p className="mt-4 max-w-[320px] text-[15px] leading-relaxed text-[#57565E]">
						More in the{' '}
						<Link
							to="/guides/find-competitor-backlinks"
							className="text-brand font-medium underline underline-offset-2"
						>
							guides
						</Link>
						, or just{' '}
						<Link
							to="/free-backlink-checker"
							className="text-brand font-medium underline underline-offset-2"
						>
							run a free check
						</Link>
						.
					</p>
				</div>
				<FaqAccordion faqs={HOME_FAQS} />
			</section>

			{/* BIG CTA */}
			<section className="mx-auto max-w-[1080px] px-8 pt-[84px]">
				<div className="bg-ink relative overflow-hidden rounded-[22px] px-11 py-[60px] text-center text-[#F3F1EC]">
					<svg
						width="100%"
						height="100%"
						viewBox="0 0 900 300"
						preserveAspectRatio="xMidYMid slice"
						className="absolute inset-0 opacity-40"
					>
						<g stroke="#7A5CFF" strokeWidth="1" opacity="0.5">
							<line x1="450" y1="150" x2="100" y2="40" />
							<line x1="450" y1="150" x2="820" y2="60" />
							<line x1="450" y1="150" x2="150" y2="260" />
							<line x1="450" y1="150" x2="800" y2="250" />
							<line x1="450" y1="150" x2="300" y2="30" />
						</g>
						<g fill="#5B4BFF">
							<circle cx="100" cy="40" r="5" />
							<circle cx="820" cy="60" r="6" />
							<circle cx="150" cy="260" r="5" />
							<circle cx="800" cy="250" r="5" />
							<circle cx="300" cy="30" r="4" />
						</g>
					</svg>
					<div className="relative">
						<Heading className="mb-4 text-[clamp(30px,4.5vw,46px)] leading-[1.08]">
							See a competitor&rsquo;s link network
							<br />
							in the next 60 seconds
						</Heading>
						<p className="mx-auto mb-7 max-w-[520px] text-lg text-[#B9B6AE]">
							Free, no account, and genuinely useful before we ever ask for your
							email.
						</p>
						<Link
							to="/free-backlink-checker"
							className="bg-brand inline-block rounded-xl px-8 py-4 text-base font-semibold text-white hover:opacity-90"
						>
							Try the free checker →
						</Link>
					</div>
				</div>
			</section>

			<Footer />
		</div>
	)
}
